import React, { useState } from 'react';
import { INVOICES, Invoice } from '../data';
import RowChevronMenu from './RowChevronMenu';
import ActivityDrawer from './ActivityDrawer';
import ColumnsPicker, { ColumnKey, DEFAULT_VISIBLE } from './ColumnsPicker';
import QuickLinksMenu from './QuickLinksMenu';

const H1: React.CSSProperties = { fontSize: 26, fontWeight: 800, margin: '0 0 6px', color: '#0E1A1A' };
const SUB: React.CSSProperties = { margin: 0, fontSize: 14, color: '#66748B', maxWidth: 560 };
const PAGE_HEAD: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
  gap: 24, marginBottom: 22,
};
const BTN: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  background: '#0E1A1A', color: '#FFFFFF',
  fontSize: 14, fontWeight: 600, padding: '11px 18px',
  borderRadius: 10, border: 0, cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(14,26,26,.12)',
};
const PROG: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20,
  padding: '16px 18px', background: '#FFFFFF',
  border: '1px solid #E7EAF0', borderRadius: 10, marginBottom: 14,
};
const PROG_LBL: React.CSSProperties = { fontSize: 11, color: '#66748B', fontWeight: 700, marginBottom: 8 };
const PROG_AMT: React.CSSProperties = { fontSize: 18, fontWeight: 800 };
const PROG_ROW: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 10 };
const BAR: React.CSSProperties = { height: 6, borderRadius: 3, background: '#F0F2F5', overflow: 'hidden', marginTop: 6, display: 'flex' };

const TOOLBAR: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  background: '#FFFFFF', border: '1px solid #E7EAF0',
  borderRadius: '14px 14px 0 0', padding: '12px 18px', borderBottom: 0,
};
const CHIP: React.CSSProperties = {
  fontSize: 12.5, padding: '6px 12px', borderRadius: 999,
  color: '#66748B', background: '#F7F8FA', cursor: 'pointer',
};
const CHIP_ACTIVE: React.CSSProperties = {
  ...CHIP, background: '#0E1A1A', color: '#FFFFFF', fontWeight: 600,
};
const TOOLS: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'center' };
const IC: React.CSSProperties = {
  width: 32, height: 32, borderRadius: 8,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: '#66748B', cursor: 'pointer',
  background: '#FFFFFF', border: '1px solid #E7EAF0',
};
const TABLE_WRAP: React.CSSProperties = {
  background: '#FFFFFF', border: '1px solid #E7EAF0', borderTop: 0,
  borderRadius: '0 0 14px 14px', overflow: 'visible',
};
const TBL: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' };
const TH: React.CSSProperties = {
  textAlign: 'left', fontSize: 11, fontWeight: 700,
  color: '#66748B', letterSpacing: 0.3, textTransform: 'uppercase',
  padding: '12px 14px', borderBottom: '1px solid #F0F2F5',
};
const TD: React.CSSProperties = {
  fontSize: 13, color: '#0E1A1A',
  padding: '14px', borderBottom: '1px solid #F0F2F5',
  verticalAlign: 'middle',
};
const BADGE_BASE: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, padding: '3px 9px',
  borderRadius: 999, textTransform: 'uppercase', letterSpacing: 0.3,
};
const STATUS_STYLES: Record<Invoice['status'], React.CSSProperties> = {
  overdue: { ...BADGE_BASE, background: '#FEF2F2', color: '#B91C1C' },
  due:     { ...BADGE_BASE, background: '#FEFCE8', color: '#A16207' },
  paid:    { ...BADGE_BASE, background: '#DCFCE7', color: '#0F6E56' },
  draft:   { ...BADGE_BASE, background: '#F1F5F9', color: '#64748B' },
};
const CHEV_BTN: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: 'transparent', border: 0, color: '#66748B',
  cursor: 'pointer', fontSize: 12, fontWeight: 600,
  padding: '4px 8px', borderRadius: 6,
};

type FilterKey = 'all' | 'overdue' | 'due' | 'paid' | 'draft';
const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all',     label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'due',     label: 'Due' },
  { key: 'paid',    label: 'Paid' },
  { key: 'draft',   label: 'Draft' },
];

export default function InvoicesList() {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [visible, setVisible] = useState<Record<ColumnKey, boolean>>(DEFAULT_VISIBLE);
  const [chevronOpen, setChevronOpen] = useState<string | null>(null);
  const [drawerFor, setDrawerFor] = useState<Invoice | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);

  const rows = filter === 'all' ? INVOICES : INVOICES.filter((i) => i.status === filter);
  const overdueTotal = INVOICES.filter((i) => i.status === 'overdue').reduce((s, i) => s + i.amount, 0);
  const paidTotal    = INVOICES.filter((i) => i.status === 'paid').reduce((s, i) => s + i.amount, 0);
  const grandTotal   = INVOICES.reduce((s, i) => s + i.amount, 0);
  const pct = grandTotal === 0 ? 0 : Math.round((paidTotal / grandTotal) * 100);

  return (
    <div>
      <div style={PAGE_HEAD}>
        <div>
          <h1 style={H1}>Invoices</h1>
          <p style={SUB}>Every invoice you've sent, grouped by status. Click a row to open its activity.</p>
        </div>
        <button type="button" style={BTN}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M12 5v14M5 12h14" />
          </svg>
          New invoice
        </button>
      </div>

      <div style={PROG}>
        <div>
          <div style={PROG_LBL}>Collected this cycle</div>
          <div style={PROG_AMT}>
            ${paidTotal.toLocaleString()}
            <span style={{ fontSize: 13, color: '#66748B', fontWeight: 500 }}> of ${grandTotal.toLocaleString()}</span>
          </div>
          <div style={PROG_ROW}>
            <span style={{ color: '#66748B' }}>{pct}% collected</span>
            <span style={{ color: '#66748B' }}>Target 90%</span>
          </div>
          <div style={BAR}>
            <div style={{ width: `${pct}%`, background: '#0F6E56' }} />
            <div style={{ width: `${100 - pct}%`, background: '#F0F2F5' }} />
          </div>
        </div>
        <div>
          <div style={PROG_LBL}>Overdue amount</div>
          <div style={{ ...PROG_AMT, color: '#B91C1C' }}>${overdueTotal.toLocaleString()}</div>
          <div style={PROG_ROW}>
            <span style={{ color: '#66748B' }}>{INVOICES.filter((i) => i.status === 'overdue').length} invoices</span>
            <span style={{ color: '#0F6E56', fontWeight: 600, cursor: 'pointer' }}>Chase in Chase hub {'\u2192'}</span>
          </div>
        </div>
      </div>

      <div style={TOOLBAR}>
        <div style={{ display: 'flex', gap: 8 }}>
          {FILTERS.map((f) => (
            <div
              key={f.key}
              style={filter === f.key ? CHIP_ACTIVE : CHIP}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </div>
          ))}
        </div>
        <div style={TOOLS}>
          <div style={IC} title="Sort">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M6 12h12M10 18h4" />
            </svg>
          </div>
          <div style={{ position: 'relative' }}>
            <div style={IC} title="Columns" onClick={() => setColumnsOpen((o) => !o)}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <path d="M9 4v16M15 4v16" />
              </svg>
            </div>
            {columnsOpen && (
              <ColumnsPicker
                visible={visible}
                onChange={setVisible}
                onClose={() => setColumnsOpen(false)}
              />
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <div style={IC} title="More" onClick={() => setQuickOpen((o) => !o)}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <circle cx="5"  cy="12" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="19" cy="12" r="2" />
              </svg>
            </div>
            {quickOpen && (
              <QuickLinksMenu onClose={() => setQuickOpen(false)} onSelect={() => undefined} />
            )}
          </div>
        </div>
      </div>

      <div style={TABLE_WRAP}>
        {rows.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#66748B' }}>
            No invoices match this filter.
          </div>
        ) : (
          <table style={TBL}>
            <thead>
              <tr>
                {visible.number   && <th style={TH}>Invoice</th>}
                {visible.customer && <th style={TH}>Customer</th>}
                {visible.issued   && <th style={TH}>Issued</th>}
                {visible.due      && <th style={TH}>Due</th>}
                {visible.amount   && <th style={{ ...TH, textAlign: 'right' }}>Amount</th>}
                {visible.status   && <th style={TH}>Status</th>}
                <th style={{ ...TH, width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr
                  key={inv.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setDrawerFor(inv)}
                  onMouseOver={(e) => (e.currentTarget.style.background = '#FAFBFD')}
                  onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  {visible.number   && <td style={TD}><b>{inv.number}</b></td>}
                  {visible.customer && <td style={TD}>{inv.customer}</td>}
                  {visible.issued   && <td style={{ ...TD, color: '#66748B' }}>{inv.issuedOn}</td>}
                  {visible.due      && <td style={{ ...TD, color: '#66748B' }}>{inv.dueOn}</td>}
                  {visible.amount   && <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>${inv.amount.toLocaleString()}</td>}
                  {visible.status   && <td style={TD}><span style={STATUS_STYLES[inv.status]}>{inv.status}</span></td>}
                  <td style={{ ...TD, textAlign: 'right', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      style={CHEV_BTN}
                      onClick={() => setChevronOpen((c) => (c === inv.id ? null : inv.id))}
                      onMouseOver={(e) => (e.currentTarget.style.background = '#F7F8FA')}
                      onMouseOut={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    {chevronOpen === inv.id && (
                      <RowChevronMenu
                        invoice={inv}
                        onClose={() => setChevronOpen(null)}
                        onAction={() => undefined}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {drawerFor && <ActivityDrawer invoice={drawerFor} onClose={() => setDrawerFor(null)} />}
    </div>
  );
}